import type { Company, CompanyMember } from '../types/company';
import { collectDescendantCompanyIds } from './companyHierarchy';

/**
 * #81 — resolve a uid to a human display name for the "created by {name}" /
 * "last edited by {name}" provenance tags.
 *
 * Pure, dependency-free, UI-and-store shared. Resolution order:
 *   1. `companyMembers[uid].displayName` (preferred — the canonical name).
 *   2. `fallbackEmail` (the email embedded on the stamp, e.g.
 *      `createdBy.email`, when the editor is not a known company member).
 *   3. `undefined` — caller renders nothing (legacy / unknown editor).
 *
 * A blank/whitespace `displayName` is treated as absent so a member record
 * with an empty name still falls through to the email.
 *
 * @param uid           The editor/creator uid (may be undefined on legacy stamps).
 * @param members       The store's `companyMembers` list.
 * @param fallbackEmail Email captured on the stamp, used when no member matches.
 */
export function resolveMemberName(
  uid: string | undefined,
  members: CompanyMember[],
  fallbackEmail?: string,
): string | undefined {
  if (typeof uid === 'string' && uid.trim().length > 0) {
    const member = members.find((m) => m.uid === uid);
    const name = member?.displayName;
    if (typeof name === 'string' && name.trim().length > 0) return name;
  }
  if (typeof fallbackEmail === 'string' && fallbackEmail.trim().length > 0) {
    return fallbackEmail;
  }
  return undefined;
}

/** Stamp sites/reports with the signed-in user's org for company-shared access. */
export function resolveOrgStamp(
  explicitCompanyId: string | null | undefined,
  fallbackCompanyId: string | null | undefined,
): { companyId?: string; isCompanyShared?: boolean } {
  const companyId =
    (typeof explicitCompanyId === 'string' && explicitCompanyId.trim().length > 0
      ? explicitCompanyId.trim()
      : null)
    ?? (typeof fallbackCompanyId === 'string' && fallbackCompanyId.trim().length > 0
      ? fallbackCompanyId.trim()
      : null);
  if (!companyId) return {};
  return { companyId, isCompanyShared: true };
}

/**
 * The orgs the signed-in user may assign a NEW site to: their own company
 * plus its descendant sub-orgs ONLY. Never a parent or sibling — this
 * mirrors `isInCompanyTree` in firestore.rules and never widens beyond the
 * named subtree (no cross-tenant leakage).
 *
 * The user's own company is pinned first (so it's the natural default);
 * descendants follow alphabetically. When the user's own company is not
 * present in `universe` (e.g. a non-admin whose company has no sites yet),
 * a minimal placeholder is synthesised from `ownCompanyName` so it can still
 * anchor the picker.
 *
 * Returns [] when the user has no companyId, which callers treat as
 * "no picker — assign to the user's own org as today".
 *
 * @param universe        Known company records in scope (admin: full list;
 *                        non-admin: the company cards already loaded).
 * @param userCompanyId   The signed-in user's company id.
 * @param ownCompanyName  Display name for the user's own company, used only
 *                        when that company is missing from `universe`.
 */
export function resolveAssignableCompanies(
  universe: Company[],
  userCompanyId: string | null | undefined,
  ownCompanyName?: string | null,
): Company[] {
  if (typeof userCompanyId !== 'string' || userCompanyId.trim().length === 0) {
    return [];
  }
  const allowedIds = collectDescendantCompanyIds(universe, userCompanyId);
  const byId = new Map(universe.map((c) => [c.id, c]));
  const rows: Company[] = [];
  allowedIds.forEach((id) => {
    const company = byId.get(id);
    if (company) {
      rows.push(company);
    } else if (id === userCompanyId) {
      rows.push({
        id,
        name: ownCompanyName ?? id,
        parentCompanyId: null,
        features: { siteRag: true, simulation: true },
        settings: { defaultRole: 'viewer', approvalRequired: false },
        createdAt: '',
        createdBy: '',
      });
    }
  });
  return rows.sort((a, b) => {
    if (a.id === userCompanyId) return -1;
    if (b.id === userCompanyId) return 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
}

/**
 * Resolve the companyId to stamp on a new site from the picker selection.
 * Honours `selected` only when it is genuinely assignable (in the user's
 * own subtree). Anything else (stale state, picker hidden, empty) returns
 * `undefined` so `resolveOrgStamp` falls back to the user's own companyId —
 * preserving the pre-picker behaviour exactly.
 */
export function resolveAssignedCompanyId(
  selected: string | null | undefined,
  assignable: Company[],
): string | undefined {
  if (typeof selected !== 'string' || selected.trim().length === 0) return undefined;
  return assignable.some((c) => c.id === selected) ? selected : undefined;
}
